import {testIdProps} from '@github-ui/test-id-props'
import {PlusIcon} from '@primer/octicons-react'
import {IconButton, useConfirm, useRefObjectAsForwardedRef} from '@primer/react'
import {clsx} from 'clsx'
import {type ComponentPropsWithoutRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import {ItemType} from '../../api/memex-items/item-type'
import type {RepositoryItem, SuggestedRepository} from '../../api/repository/contracts'
import type {GroupingMetadataWithSource} from '../../features/grouping/types'
import {useApplyTemplate} from '../../features/templates/hooks/use-apply-template'
import {replaceShortCodesWithEmojis} from '../../helpers/emojis'
import {ViewType} from '../../helpers/view-type'
import {useAddMemexItem, type UseAddMemexItemProps} from '../../hooks/use-add-memex-item'
import {useAutoAddSubIssueWorkflowToast} from '../../hooks/use-auto-add-sub-issue-toast'
import {useViewType} from '../../hooks/use-view-type'
import type {MemexItemModel} from '../../models/memex-item-model'
import {useCreateMemexItem} from '../../state-providers/memex-items/use-create-memex-item'
import {useRepositories} from '../../state-providers/repositories/use-repositories'
import {useTemplateDialog} from '../../state-providers/template-dialog/use-template-dialog'
import {ParentIssueReassignmentConfirmationResources, Resources} from '../../strings'
import {BorderlessTextInput} from '../common/borderless-text-input'
import {DiscoveryInput} from '../discovery-suggestions'
import {useSearch} from '../filter-bar/search-context'
import {useStartIssueCreator} from '../issue-creator'
import {OnboardingPopover, useOnboardingPopover} from '../onboarding-popover'
import type {RenderInput} from '../suggested-item-picker'
import {SuggestionsForRepository} from '../suggestions-for-repository'
import {CELL_HEIGHT} from '../table/constants'
import {clearFocus, useStableTableNavigation} from '../table/navigation'
import {RepoSearcher} from '../table/repo-searcher'
import useToasts, {ToastType} from '../toasts/use-toasts'
import {draftsUnsupported, getDraftItemsWarningMessage} from './helpers'
import styles from './omnibar.module.css'
import type {OmnibarItemAttrs, OmnibarMenu} from './types'

// This was originally `CELL_HEIGHT + 8`, but that no longer makes sense as this
// is not a table-specific component.
export const OMNIBAR_HEIGHT = 45

// Padding used to offset the omnibar from the bottom of the viewport.
export const OMNIBAR_SIBLING_PADDING_BOTTOM = `${OMNIBAR_HEIGHT + 24}px`

type OmnibarProps = {
  /** Reference to the current scrollable body */
  scrollRef?: React.RefObject<HTMLElement | null>
  /** A callback called when a new item was added to the Memex via the Omnibar */
  onAddItem?: (model: MemexItemModel) => void
  /** What field, if any, to set on new items, and what their previous item ID is */
  newItemAttributes?: OmnibarItemAttrs
  /** Is the omnibar fixed to the bottom of the table view */
  isFixed?: boolean
  /** The default placeholder to show when the omnibar does not have focus */
  defaultPlaceholder: React.ReactNode
  /** Grouped-by metadata, such as repo **/
  groupingMetadata?: GroupingMetadataWithSource
  /** Is the omnibar input disabled */
  disabled?: boolean
  /** Callback for when the input within the Omnibar receives focus */
  onInputFocus?: () => void
  /**
   * An aria role passed to the children of the omnibar wrapper.
   *
   * useful when passing a role to the omnibar that requires specific
   * child roles, like `row` requiring `cell` or `gridcell` type children
   */
  childElementRole?: string
}

export type OmnibarRef = {
  /** Focus the omnibar's input. */
  focus: () => void
  /** Blur the omnibar's input. */
  blur: () => void
  /** Whether or not the target passed is the input element of the omnibar */
  isInputElement: (target: EventTarget | null) => boolean
}

export const Omnibar = memo(
  ({
    ref,
    onAddItem,
    newItemAttributes,
    scrollRef,
    ...props
  }: OmnibarProps & ComponentPropsWithoutRef<'div'> & {ref: React.Ref<OmnibarRef | null>}) => {
    const {addToast} = useToasts()
    const confirm = useConfirm()
    const {createMemexItem} = useCreateMemexItem()
    const showAutoAddSubIssueToast = useAutoAddSubIssueWorkflowToast()
    const {fieldFilters} = useSearch()
    const parentIssueFilter = fieldFilters.find(filter => filter[0] === 'parent-issue')
    // If the omnibar is grouped by parent issue, the grouping metadata will have
    // dataType === MemexColumnDataType.ParentIssue and its `value` will be the
    // parent's nwoReference. When grouped by parent, adding an item that
    // already belongs to a different parent should also trigger the same
    // reassignment confirmation as when a parent-issue filter is active.
    const groupingParentValue =
      props.groupingMetadata?.sourceObject?.dataType === MemexColumnDataType.ParentIssue
        ? props.groupingMetadata.value
        : undefined

    const wrappedOnAddItem: BaseOmnibarProps['onAddItem'] = useCallback(
      async (
        item,
        repositoryId,
        memexProjectColumnValues,
        localColumnValues,
        previousMemexProjectItemId,
        groupId,
        secondaryGroupId,
      ) => {
        const shouldAddSubIssue = async () => {
          // No need for confirmation if there's neither a parent filter nor a
          // grouping-by-parent active
          if (!parentIssueFilter && groupingParentValue === undefined) return true

          // Item has no parent
          if (!item.parentNwo) return true

          // If there's a parent filter and it matches the item's parent, allow
          // adding without confirmation
          if (parentIssueFilter && parentIssueFilter[1].includes(item.parentNwo)) return true

          // If we're grouped by a parent and the group's value matches the
          // item's parent, allow adding without confirmation
          if (groupingParentValue && groupingParentValue === item.parentNwo) return true

          // Item has a different parent and we have either a parent filter or
          // are grouped by parent, so require confirmation
          return await confirm({
            title: ParentIssueReassignmentConfirmationResources.title,
            content: ParentIssueReassignmentConfirmationResources.content,
            confirmButtonType: 'primary',
            confirmButtonContent: ParentIssueReassignmentConfirmationResources.confirmButtonContent,
          })
        }

        if (!(await shouldAddSubIssue())) {
          return null
        }

        const model = await createMemexItem(
          {
            contentType: item.type,
            content: {id: item.id, repositoryId},
            memexProjectColumnValues,
            localColumnValues,
            previousMemexProjectItemId,
          },
          groupId,
          secondaryGroupId,
        )
        onAddItem?.(model)
        if (item.type === 'Issue' && item.hasSubIssues) {
          showAutoAddSubIssueToast()
        }
        return model
      },
      [confirm, createMemexItem, onAddItem, parentIssueFilter, showAutoAddSubIssueToast, groupingParentValue],
    )

    const wrappedOnAddDraftItem: BaseOmnibarProps['onAddDraftItem'] = useCallback(
      async (
        text,
        memexProjectColumnValues,
        localColumnValues,
        previousMemexProjectItemId,
        groupId,
        secondaryGroupId,
      ) => {
        const textValue = text.trim()

        const {updateColumnActions = []} = newItemAttributes ?? {}

        if (textValue) {
          const model = await createMemexItem(
            {
              contentType: ItemType.DraftIssue,
              content: {title: replaceShortCodesWithEmojis(textValue)},
              memexProjectColumnValues,
              localColumnValues,
              previousMemexProjectItemId,
            },
            groupId,
            secondaryGroupId,
          )
          onAddItem?.(model)
          for (const updateAction of updateColumnActions) {
            // Some groupings don't support draft items, when they are added the drafts are added to the "no group" group
            // If adding draft text as a link, confirm whether or not added item is an issue
            // before scrolling to "No <Group>" group and displaying toast warning messages
            if (draftsUnsupported(updateAction) && model.contentType !== ItemType.Issue) {
              const message = getDraftItemsWarningMessage(updateAction)
              if (message) {
                // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
                addToast({message, type: ToastType.warning})
              }

              scrollRef?.current?.scrollTo({left: 0, top: scrollRef.current?.scrollHeight, behavior: 'smooth'})
              break
            }
          }
          return model
        }
        return Promise.resolve(null)
      },
      [addToast, createMemexItem, newItemAttributes, onAddItem, scrollRef],
    )

    return (
      <BaseOmnibar
        onAddItem={wrappedOnAddItem}
        onAddDraftItem={wrappedOnAddDraftItem}
        newItemAttributes={newItemAttributes}
        {...props}
        ref={ref}
      />
    )
  },
)

Omnibar.displayName = 'OmnibarInternal'

type BaseOmnibarProps = Omit<OmnibarProps, 'onAddItem'> &
  ComponentPropsWithoutRef<'div'> &
  Pick<UseAddMemexItemProps, 'onAddDraftItem' | 'onAddItem'> & {
    filteredItemIds?: Array<number>
  }

/**
 * The internal content of the Omnibar component
 */
const BaseOmnibar = ({
  ref: fowardedRef,
  onAddItem,
  onAddDraftItem,
  onInputFocus,
  filteredItemIds,
  newItemAttributes,
  isFixed,
  defaultPlaceholder,
  groupingMetadata,
  disabled,
  childElementRole,
  ...props
}: BaseOmnibarProps & {ref: React.Ref<OmnibarRef | null>}) => {
  const ref = useRef<OmnibarRef>(null)
  useRefObjectAsForwardedRef(fowardedRef, ref)
  const [isFocused, setIsFocused] = useState(false)

  const [omnibarMenu, setOmnibarMenu] = useState<OmnibarMenu>(null)
  const {navigationDispatch} = useStableTableNavigation()
  const startIssueCreator = useStartIssueCreator()

  const {setShouldShowOnboardingPopover, shouldShowOnboardingPopover} = useOnboardingPopover()
  const [showPopover, setShowPopover] = useState(false)

  const onInputClick = useCallback(() => {
    ref.current?.focus()
  }, [ref])

  const omnibarClassName = clsx(isFocused && styles.isFocused, isFixed && styles.isFixed)
  const buttonClassName = clsx(isFocused && styles.isFocused)

  const {isTemplatesDialogOpen} = useTemplateDialog()
  const {copyingDraftsAsync} = useApplyTemplate()
  const {viewType} = useViewType()

  // If the template dialog should be open, then we will show a popover with a hint to start adding items.
  useEffect(() => {
    if (!shouldShowOnboardingPopover) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowPopover(false)
      return
    }
    // When template dialog is closed, we will show the onboarding popover. Do not show it if the user is in board view,
    // since the board view has a floating omnibar. Also don't show if copying drafts async, as live updates will soon
    // add items to the project. We also do not show if the omnibar is floating.
    if (viewType === ViewType.Table && !isTemplatesDialogOpen && !copyingDraftsAsync.current && !isFixed) {
      setShowPopover(true)
    }
  }, [copyingDraftsAsync, isFixed, isTemplatesDialogOpen, viewType, setShowPopover, shouldShowOnboardingPopover])

  const handleBlur = useCallback(() => {
    setIsFocused(false)

    // The repoSearch and selectedRepo modes have their own mechanism for blurring, that keep their menu states when focus is returned
    if (omnibarMenu === 'discovery') {
      setOmnibarMenu(null)
    }
  }, [setIsFocused, omnibarMenu, setOmnibarMenu])

  const newItemButtonOnEnter = useCallback(() => {
    startIssueCreator?.prefetch()
  }, [startIssueCreator])

  const wrappedOnInputFocus = useCallback(() => {
    if (onInputFocus) {
      onInputFocus()
    }

    setIsFocused(true)
  }, [onInputFocus])

  const dismissPopover = useCallback(() => {
    setShouldShowOnboardingPopover(false)
  }, [setShouldShowOnboardingPopover])

  const newItemButtonOnClick = useCallback(
    (e: React.MouseEvent) => {
      // We only want to show the discovery menu if the user is currently in base mode
      if (omnibarMenu !== null) {
        return
      }
      // The event is not bubbled up in order to not trigger blur on the input which would cause setOmnibarMenu(null) to be called
      e.preventDefault()
      setOmnibarMenu('discovery')
      dismissPopover()

      // Update internal focused state and trigger parent component's focus handler (onInputFocus)
      wrappedOnInputFocus()
      // Focus the DOM element
      ref.current?.focus()
    },
    [omnibarMenu, wrappedOnInputFocus, dismissPopover, ref],
  )

  const newItemButtonOnFocus: React.EventHandler<React.FocusEvent> = useCallback(() => {
    // since the button is within the table, we need to manually
    // unfocus the last cell in the table when this button receives focus
    navigationDispatch(clearFocus())
  }, [navigationDispatch])

  const onboardingPopoverAddItem = useCallback(
    (event: React.MouseEvent) => {
      dismissPopover()
      newItemButtonOnClick(event)
    },
    [newItemButtonOnClick, dismissPopover],
  )
  const onPopoverDismiss = useCallback(() => {
    dismissPopover()
  }, [dismissPopover])

  return (
    <div
      role={childElementRole}
      className={clsx(omnibarClassName, styles.omnibar)}
      style={{
        '--cell-height': `${CELL_HEIGHT}px`,
        '--omnibar-height': `${OMNIBAR_HEIGHT}px`,
      }}
      {...testIdProps('omnibar')}
      {...props}
    >
      <div role="cell" className={styles.addButtonContainer}>
        <IconButton
          aria-haspopup="menu"
          aria-label={Resources.createNewItemOrAddExistingIssueAriaLabel}
          className={clsx(buttonClassName, styles.addItemButton)}
          onClick={newItemButtonOnClick}
          onFocus={newItemButtonOnFocus}
          onMouseEnter={newItemButtonOnEnter}
          disabled={disabled || omnibarMenu !== null}
          {...testIdProps('new-item-button')}
          icon={PlusIcon}
          variant="invisible"
          size="small"
        />
        {showPopover && <OnboardingPopover addItem={onboardingPopoverAddItem} onDismiss={onPopoverDismiss} />}
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div role="cell" className={clsx(omnibarClassName, styles.inputContainer)} onClick={onInputClick}>
        <OmnibarInput
          ref={ref}
          isFocused={isFocused}
          filteredItemIds={filteredItemIds}
          omnibarMenu={omnibarMenu}
          setOmnibarMenu={setOmnibarMenu}
          onAddItem={onAddItem}
          onAddDraftItem={onAddDraftItem}
          newItemAttributes={newItemAttributes}
          onFocus={wrappedOnInputFocus}
          onBlur={handleBlur}
          defaultPlaceholder={defaultPlaceholder}
          groupingMetadata={groupingMetadata}
          disabled={disabled}
        />
      </div>
    </div>
  )
}

BaseOmnibar.displayName = 'BaseOmnibar'

type OmnibarInputProps = Pick<UseAddMemexItemProps, 'onAddDraftItem' | 'onAddItem'> & {
  ref?: React.Ref<OmnibarRef>
  /** Additional item ids to filter (supporting non-memex items) */
  filteredItemIds?: Array<number>
  /** A callback called when the input becomes focused */
  onFocus?: () => void
  /** A callback called when the input becomes un-focused */
  onBlur?: () => void
  /** What field, if any, to set on new items, and what their previous item ID is */
  newItemAttributes?: OmnibarItemAttrs
  /** The default placeholder to show when the omnibar does not have focus */
  defaultPlaceholder: React.ReactNode
  /** Grouped-by metadata, such as repo **/
  groupingMetadata?: GroupingMetadataWithSource
  /** Default value for focused state, passed from the Parent to OmnibarInput */
  isFocused?: boolean
  /** Toggle between discovery, repo selection, and repo item selection */
  omnibarMenu: OmnibarMenu
  /** Callback to change the discovery input visibility */
  setOmnibarMenu: (value: OmnibarMenu) => void
  /** Whether the input is disabled*/
  disabled?: boolean
} & Pick<BaseOmnibarProps, 'isFixed'>

const defaultFilteredItemIds: Array<number> = []
/**
 * The input element the user types into in the Omnibar
 *
 * Depending on whether a repository is selected, this will either be a search
 * input for the user's repositories, or for the selected repository's issues.
 */
export const OmnibarInput = memo(
  ({
    ref,
    onAddItem,
    onAddDraftItem,
    onFocus,
    onBlur,
    filteredItemIds = defaultFilteredItemIds,
    isFocused,
    omnibarMenu,
    setOmnibarMenu,
    newItemAttributes,
    defaultPlaceholder,
    groupingMetadata,
    disabled,
  }: OmnibarInputProps) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const {clearCachedSuggestions} = useRepositories()

    const addItem = useAddMemexItem({
      updateActions: newItemAttributes?.updateColumnActions,
      previousItemId: newItemAttributes?.previousItemId,
      onAddItem,
      onAddDraftItem,
      groupId: newItemAttributes?.groupId,
      secondaryGroupId: newItemAttributes?.secondaryGroupId,
    })

    // look for a grouped-by repository and cast it from ExtendedRepository to SuggestedRepository if we find it
    // type ExtendedRepository is only missing a couple of date fields that we don't need here, so it's not a big lie
    const groupingRepository =
      groupingMetadata?.sourceObject.dataType === 'repository' && 'name' in groupingMetadata.sourceObject.value
        ? (groupingMetadata.sourceObject.value as SuggestedRepository)
        : null

    const [omnibarText, setOmnibarText] = useState('')
    const [selectedRepository, setSelectedRepository] = useState<SuggestedRepository | null>(groupingRepository)

    useEffect(() => {
      // Synchronize changes in the grouping repository with the selected repository, so that if the initial props
      // change, we automatically update the state here (otherwise, omnibar would not reflect passed-in props)
      if (groupingRepository) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedRepository(groupingRepository)
        setOmnibarMenu('items')
      }
    }, [groupingRepository, setOmnibarMenu])

    const selectRepository = useCallback(
      (repository: SuggestedRepository | null) => {
        setOmnibarMenu('items')
        setSelectedRepository(repository)
        setOmnibarText('')
        onFocus?.()
      },
      [onFocus, setOmnibarMenu],
    )

    useImperativeHandle(
      ref,
      () => ({
        focus: () => inputRef.current?.focus({preventScroll: true}),
        // eslint-disable-next-line github/no-blur
        blur: () => inputRef.current?.blur(),
        isInputElement: target => target === inputRef.current,
      }),
      [],
    )

    const selectItem = useCallback(
      async (item: RepositoryItem) => {
        // Adding an item may change which repositories should be
        // suggested for this project.
        // Clearing the cache ensures suggested repos are fetched
        // afresh from the server the next time they're required
        clearCachedSuggestions()
        setOmnibarText('')

        if (selectedRepository) {
          return addItem({...item, repositoryId: selectedRepository?.id, repositoryOwner: selectedRepository?.owner})
        }
      },
      [clearCachedSuggestions, addItem, selectedRepository],
    )

    const createDraft = useCallback(
      async (item: string, options?: Partial<{shouldFocusInput?: boolean}>) => {
        // Add draft item
        if (typeof item === 'string') {
          setOmnibarText('')
          setOmnibarMenu(null)
          await addItem(item)

          if (options?.shouldFocusInput) {
            onFocus?.()
          }
        }
      },
      [addItem, onFocus, setOmnibarMenu],
    )

    const renderInput: RenderInput = useCallback(
      props => {
        const wrappedOnFocus: React.FocusEventHandler<HTMLInputElement> = e => {
          if (props.onFocus) props.onFocus(e)
          if (onFocus) onFocus()
        }

        const wrappedOnBlur: React.FocusEventHandler<HTMLInputElement> = e => {
          if (props.onBlur) props.onBlur(e)
          if (onBlur) onBlur()
        }
        const {width, size, ...filteredProps} = props

        return (
          <BorderlessTextInput
            {...filteredProps}
            ref={inputRef}
            onFocus={wrappedOnFocus}
            onBlur={wrappedOnBlur}
            className={styles.omnibarInput}
            // TODO: We should update this name to `omnibar-input` to be clearer with new behavior. Save for own PR.
            {...testIdProps('repo-searcher-input')}
          />
        )
      },
      [onFocus, onBlur],
    )

    const removePicker = useCallback(() => {
      // Don't let the grouping repository be removed from the picker
      if (groupingRepository) {
        return
      }

      setOmnibarText('')
      setSelectedRepository(null)
      setOmnibarMenu(null)
    }, [groupingRepository, setSelectedRepository, setOmnibarMenu])

    return (
      <>
        {(omnibarMenu === null || omnibarMenu === 'discovery') && (
          <DiscoveryInput
            inputRef={inputRef}
            renderInput={renderInput}
            newItemAttributes={newItemAttributes}
            omnibarText={omnibarText}
            setOmnibarText={setOmnibarText}
            defaultPlaceholder={defaultPlaceholder}
            isDisabled={disabled}
            createDraft={createDraft}
            isFocused={isFocused}
            omnibarMenu={omnibarMenu}
            setOmnibarMenu={setOmnibarMenu}
          />
        )}
        {omnibarMenu === 'repos' && (
          <RepoSearcher
            inputRef={inputRef}
            isFocused={isFocused}
            isDisabled={disabled}
            renderInput={renderInput}
            defaultPlaceholder={defaultPlaceholder}
            omnibarText={omnibarText}
            setOmnibarText={setOmnibarText}
            onRepositorySelected={selectRepository}
            groupingMetadata={groupingMetadata}
            setOmnibarMenu={setOmnibarMenu}
          />
        )}
        {omnibarMenu === 'items' && selectedRepository && (
          <SuggestionsForRepository
            onRemovePicker={removePicker}
            filteredItemIds={filteredItemIds}
            newItemAttributes={newItemAttributes}
            onItemSelected={selectItem}
            repository={selectedRepository}
            inputRef={inputRef}
            isFocused={isFocused}
            renderInput={renderInput}
            className={styles.repositorySuggestions}
          />
        )}
      </>
    )
  },
)

OmnibarInput.displayName = 'OmnibarInput'
